/**
 * Onboarding santé — CDC §3.2.
 *
 * Complétable en moins de 3 minutes. Aucun champ médical sensible n'est
 * obligatoire pour démarrer ; chaque consentement est journalisé côté
 * backend avec horodatage et version du texte.
 *
 * Le sexe biologique n'est PAS demandé ici : il est pré-déterminé côté
 * backend à partir du genre déjà déclaré sur Life App. Les antécédents
 * saisis ici (allergies, traitements, opérations) alimentent directement
 * le dossier médical (historisation des consultations et de la médication).
 */
import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'

import { santeApi } from '../api/santeApi'
import { HEALTH_ACCENT, cardSx, darkFieldSx } from './shared'

const GOALS = [
  { value: 'fitness', label: 'Forme physique' },
  { value: 'sleep', label: 'Sommeil' },
  { value: 'stress', label: 'Gestion du stress' },
  { value: 'weight_loss', label: 'Perte de poids' },
  { value: 'weight_gain', label: 'Prise de poids' },
  { value: 'quit_addiction', label: 'Arrêter une addiction' },
  { value: 'condition_tracking', label: 'Suivi pathologie' },
  { value: 'prevention', label: 'Prévention' },
  { value: 'energy', label: 'Énergie' },
]

const CONSENTS = [
  { value: 'activity', label: 'Activité physique' },
  { value: 'sleep', label: 'Sommeil' },
  { value: 'vitals', label: 'Constantes biométriques' },
  { value: 'medical_records', label: 'Dossier médical & documents' },
  { value: 'ai_processing', label: 'Traitement par l’IA' },
]

/** Petit champ « liste de tags » : on saisit, on ajoute, on retire. */
function TagInput({ label, placeholder, items, setItems }) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const v = draft.trim()
    if (v && !items.includes(v)) setItems([...items, v])
    setDraft('')
  }
  return (
    <Box>
      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, mb: 0.75 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          sx={darkFieldSx}
        />
        <Button
          onClick={add}
          sx={{ color: HEALTH_ACCENT, textTransform: 'none', minWidth: 80 }}
        >
          Ajouter
        </Button>
      </Stack>
      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {items.map((it) => (
            <Chip
              key={it}
              label={it}
              onDelete={() => setItems(items.filter((x) => x !== it))}
              sx={{
                bgcolor: 'rgba(61,220,151,0.18)',
                color: '#fff',
                '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.6)' },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default function OnboardingView({ onDone, profile }) {
  const [height, setHeight] = useState(profile?.height_cm || '')
  const [weight, setWeight] = useState(profile?.weight_kg || '')
  const [goals, setGoals] = useState([])
  const [consents, setConsents] = useState(['activity', 'sleep'])
  const [allergies, setAllergies] = useState([])
  const [treatments, setTreatments] = useState([])
  const [surgeries, setSurgeries] = useState([])
  const [saving, setSaving] = useState(false)

  const toggleGoal = (g) =>
    setGoals((prev) =>
      prev.includes(g)
        ? prev.filter((x) => x !== g)
        : prev.length >= 3
        ? prev
        : [...prev, g],
    )

  const toggleConsent = (c) =>
    setConsents((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    )

  const submit = async () => {
    setSaving(true)
    try {
      // Informations de base (le sexe biologique est dérivé côté serveur).
      await santeApi.updateProfile({
        height_cm: height || null,
        weight_kg: weight || null,
      })
      // Objectifs prioritaires (max 3 — contrainte serveur §3.2).
      for (let i = 0; i < goals.length; i += 1) {
        await santeApi.goals.create({ goal_type: goals[i], priority: i + 1 })
      }
      // Consentements granulaires, horodatés côté backend.
      for (const cat of CONSENTS.map((c) => c.value)) {
        await santeApi.consents.create({
          category: cat,
          granted: consents.includes(cat),
        })
      }
      // Antécédents → dossier médical (allergies & opérations = conditions,
      // traitements = médication suivie).
      for (const label of allergies) {
        await santeApi.conditions.create({
          label,
          condition_type: 'allergy',
          notes: 'Saisi à l’onboarding santé.',
        })
      }
      for (const label of surgeries) {
        await santeApi.conditions.create({
          label,
          condition_type: 'surgery',
          notes: 'Saisi à l’onboarding santé.',
        })
      }
      for (const name of treatments) {
        await santeApi.treatments.create({
          name,
          notes: 'Saisi à l’onboarding santé.',
        })
      }
      await santeApi.completeOnboarding()
      onDone()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', py: 4 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <FavoriteRoundedIcon sx={{ color: HEALTH_ACCENT }} />
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 24 }}>
          Bienvenue dans votre module Santé
        </Typography>
      </Stack>
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
        Quelques informations pour personnaliser votre copilote santé. Rien
        n’est obligatoire — vous pourrez compléter votre profil plus tard.
      </Typography>

      <Box sx={{ ...cardSx, mb: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
          Informations de base
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            type="number"
            label="Taille (cm)"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            fullWidth
            sx={darkFieldSx}
          />
          <TextField
            size="small"
            type="number"
            label="Poids (kg)"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            fullWidth
            sx={darkFieldSx}
          />
        </Stack>
      </Box>

      <Box sx={{ ...cardSx, mb: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
          Antécédents médicaux
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, mb: 2 }}>
          Ces éléments alimentent votre dossier médical et le contexte de vos
          consultations. Saisissez, appuyez sur « Ajouter ».
        </Typography>
        <Stack spacing={2.5}>
          <TagInput
            label="Allergies connues"
            placeholder="Ex. pénicilline, arachide…"
            items={allergies}
            setItems={setAllergies}
          />
          <TagInput
            label="Traitements en cours"
            placeholder="Ex. Lévothyrox, Doliprane…"
            items={treatments}
            setItems={setTreatments}
          />
          <TagInput
            label="Opérations lourdes"
            placeholder="Ex. appendicectomie 2019…"
            items={surgeries}
            setItems={setSurgeries}
          />
        </Stack>
      </Box>

      <Box sx={{ ...cardSx, mb: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
          Vos objectifs prioritaires
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, mb: 1.5 }}>
          Choisissez-en jusqu’à 3 ({goals.length}/3).
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {GOALS.map((g) => (
            <Chip
              key={g.value}
              label={g.label}
              onClick={() => toggleGoal(g.value)}
              sx={{
                bgcolor: goals.includes(g.value)
                  ? HEALTH_ACCENT
                  : 'rgba(255,255,255,0.08)',
                color: goals.includes(g.value) ? '#00001d' : '#fff',
                fontWeight: 600,
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ ...cardSx, mb: 3 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
          Consentements
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, mb: 1.5 }}>
          La donnée de santé est sensible (RGPD art. 9). Vous autorisez Life
          App, catégorie par catégorie — et pouvez tout révoquer à tout moment.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {CONSENTS.map((c) => (
            <Chip
              key={c.value}
              label={c.label}
              variant={consents.includes(c.value) ? 'filled' : 'outlined'}
              onClick={() => toggleConsent(c.value)}
              sx={{
                bgcolor: consents.includes(c.value)
                  ? 'rgba(61,220,151,0.2)'
                  : 'transparent',
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </Box>
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={saving}
        onClick={submit}
        sx={{
          bgcolor: HEALTH_ACCENT,
          color: '#00001d',
          fontWeight: 700,
          py: 1.2,
          '&:hover': { bgcolor: '#2fc985' },
        }}
      >
        {saving ? 'Enregistrement…' : 'Activer mon module Santé'}
      </Button>
    </Box>
  )
}
